import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostScalar, type PostValue } from './client'
import type { Named } from './shell'

export const LISTS = ['workstations', 'servers', 'mobiles', 'inactive', 'live_devices'] as const
export type ListName = (typeof LISTS)[number]

export function isList(value: string): value is ListName {
  return (LISTS as readonly string[]).includes(value)
}

export interface Filters {
  groups: string[]
  wei: string
  available_updates: string
  installed_applications: string
  startup_applications: string
  event_viewer_errors: string
}

export const EMPTY_FILTERS: Filters = {
  groups: [],
  wei: '',
  available_updates: '',
  installed_applications: '',
  startup_applications: '',
  event_viewer_errors: '',
}

export interface TableRequest {
  list: ListName
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName?: string
  orderDir: 'asc' | 'desc'
  search: string
  filters: Filters
}

export interface TablePage<T = Record<string, unknown>> {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export interface FilterOptions {
  groups: Named[]
}

export function tableParams(r: TableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  if (r.orderName) p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const g of r.filters.groups) p.append('groups[]', g)
  for (const key of ['wei', 'available_updates', 'installed_applications', 'startup_applications', 'event_viewer_errors'] as const) {
    if (r.filters[key] !== '') p.set(key, r.filters[key])
  }
  return p
}

export function tablePageQuery(r: TableRequest) {
  return queryOptions({
    queryKey: ['opteemum', 'table', r],
    queryFn: () => apiGet<TablePage>(`/opteemum/devices-table?${tableParams(r).toString()}`),
  })
}

export function deviceRowQuery(id: number) {
  return queryOptions({
    queryKey: ['opteemum', 'device', id],
    queryFn: () => apiGet<Record<string, unknown>>(`/opteemum/devices/${id}`),
  })
}

export const filterOptionsQuery = queryOptions({
  queryKey: ['opteemum', 'filters'],
  queryFn: () => apiGet<FilterOptions>('/opteemum/device-filters'),
  staleTime: 5 * 60 * 1000,
})

export interface PageRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName?: string
  orderDir: 'asc' | 'desc'
  search: string
}

function pageParams(r: PageRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  if (r.orderName) p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

function pageQuery(kind: 'unassigned' | 'applications' | 'schedule' | 'remote', r: PageRequest) {
  return queryOptions({
    queryKey: ['opteemum', kind, r],
    queryFn: () => apiGet<TablePage>(`/opteemum/${kind}-table?${pageParams(r)}`),
  })
}

/** Ce intoarce oricare din cele patru liste de mai jos - una si aceeasi forma. */
export type PageQueryOptions = ReturnType<typeof pageQuery>

export function unassignedQuery(r: PageRequest) {
  return pageQuery('unassigned', r)
}

export function applicationsQuery(r: PageRequest) {
  return pageQuery('applications', r)
}

export function scheduleQuery(r: PageRequest) {
  return pageQuery('schedule', r)
}

export function remoteQuery(r: PageRequest) {
  return pageQuery('remote', r)
}

/* --- Randul desfacut al unei programari, si scrierile ei --------------- */

export interface ScheduleCommand {
  ID: number
  command_title: string
  info: string | null
}

export interface ScheduleGroup {
  id: number
  name: string
  commands: ScheduleCommand[]
}

export interface SchedulePicked {
  inform_user: number
  commands: number[]
}

export interface ScheduleOperations {
  schedule_id: number
  groups: ScheduleGroup[]
  picked: Record<string, SchedulePicked>
}

/** Cele patru feluri de programare, cu campurile pe care le stie agentul. */
export interface ScheduleAlgorithm {
  daily?: { active?: string | number; time?: string }
  weekly?: { active?: string | number; day?: string | number; time?: string }
  monthly?: { active?: string | number; day?: string | number; time?: string }
  once?: { active?: string | number; datetime?: string }
}

export interface ScheduleSettings {
  schedule_id: number
  schedule_name: string
  algorithm: ScheduleAlgorithm
}

export interface ScheduleDevices {
  schedule_id: number
  devices: { ID: number; Denumire: string; UserLogat: string }[]
  picked: number[]
}

export function scheduleOperationsQuery(id: number) {
  return queryOptions({
    queryKey: ['opteemum', 'schedule', id, 'operations'],
    queryFn: () => apiGet<ScheduleOperations>(`/opteemum/schedule/${id}/operations`),
  })
}

export function scheduleSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['opteemum', 'schedule', id, 'settings'],
    queryFn: () => apiGet<ScheduleSettings>(`/opteemum/schedule/${id}/settings`),
  })
}

export function scheduleDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['opteemum', 'schedule', id, 'devices'],
    queryFn: () => apiGet<ScheduleDevices>(`/opteemum/schedule/${id}/devices`),
  })
}

export interface ScheduleWriteResult {
  status: string
  message: string | null
  schedule_id?: number
  removed?: number
}

export function createSchedule(name: string): Promise<ScheduleWriteResult> {
  return apiPost<ScheduleWriteResult>('/opteemum/schedule/create', { schedule_name: name })
}

export function removeSchedules(ids: number[]): Promise<ScheduleWriteResult> {
  return apiPost<ScheduleWriteResult>('/opteemum/schedule/remove', { 'schedules[]': ids })
}

/**
 * Campurile pleaca exact ca din formularul vechi -
 * `operations[<grup>][commands][]` si `operations[<grup>][inform_user]` -
 * fiindca asa le citeste si capatul: `$request->request->all('operations')`.
 */
export function saveScheduleOperations(id: number, picked: Record<number, SchedulePicked>): Promise<ScheduleWriteResult> {
  const fields: Record<string, PostValue> = {}
  for (const [groupId, group] of Object.entries(picked)) {
    if (group.commands.length === 0) continue
    fields[`operations[${groupId}][commands][]`] = group.commands
    if (group.inform_user === 1) fields[`operations[${groupId}][inform_user]`] = 1
  }
  return apiPost<ScheduleWriteResult>(`/opteemum/schedule/${id}/operations`, fields)
}

export function saveScheduleSettings(id: number, algorithm: ScheduleAlgorithm): Promise<ScheduleWriteResult> {
  const fields: Record<string, PostValue> = {}
  for (const [type, options] of Object.entries(algorithm)) {
    if (!options) continue
    for (const [key, value] of Object.entries(options)) {
      if (value === undefined || value === '') continue
      fields[`algorithm[${type}][${key}]`] = value as PostScalar
    }
  }
  return apiPost<ScheduleWriteResult>(`/opteemum/schedule/${id}/settings`, fields)
}

export function saveScheduleDevices(id: number, deviceIds: number[]): Promise<ScheduleWriteResult> {
  return apiPost<ScheduleWriteResult>(`/opteemum/schedule/${id}/devices`, { 'devices[]': deviceIds })
}

/** Un kit de client, asa cum il compune `ClientDownload::linksFrom`. */
export interface RemoteDownloadLink {
  label: string
  icon: string
  url: string
  hint: string
}

export interface RemoteDownloads {
  allowed: boolean
  tool: string
  links: RemoteDownloadLink[]
}

export const remoteDownloadsQuery = queryOptions({
  queryKey: ['opteemum', 'remote', 'downloads'],
  queryFn: () => apiGet<RemoteDownloads>('/opteemum/remote-downloads'),
  staleTime: 5 * 60 * 1000,
})

export interface CmdPage {
  device: { id: number }
  available: boolean
  is_linux: boolean
  asleep: boolean
  os_family: string
  sections: unknown[]
  sections_note: string
  commands_href: string
}

export function cmdQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'cmd'],
    queryFn: () => apiGet<CmdPage>(`/devices/${id}/cmd`),
  })
}

export interface AiTab {
  href: string
  icon: string
  label: string
}

export interface AiActionsPage {
  device: { id: number }
  kind: string
  inactive: boolean
  has_remote_operations: boolean
  tabs: AiTab[]
}

export function aiActionsQuery(id: number, kind: string) {
  return queryOptions({
    queryKey: ['device', id, 'ai-actions', kind],
    queryFn: () => apiGet<AiActionsPage>(`/devices/${id}/ai-actions/${kind}`),
  })
}

/* --- Widgetul de selectie multipla (Opteemum::multipleActions) --------- */

export interface MultiCommand {
  command: string
  command_title: string
  info: string
  checked: boolean
}

export interface MultiCommandGroup {
  id: number
  group_name: string
  commands: MultiCommand[]
}

export interface MultiRemoteOpsPage {
  devices: Array<{ id: number; name: string }>
  /** Macar o masina din selectie e Linux / macOS. */
  unix: boolean
  groups: MultiCommandGroup[]
}

/**
 * Ce se poate cere TUTUROR masinilor bifate. Serverul taie lista pe platforma
 * si refuza selectia intreaga daca un ID nu e al firmei alese.
 */
export function multiRemoteOpsQuery(ids: number[]) {
  const sorted = [...ids].sort((a, b) => a - b)
  return queryOptions({
    queryKey: ['opteemum', 'multi', 'remote-ops', sorted],
    queryFn: () => {
      const p = new URLSearchParams()
      for (const id of sorted) p.append('devices[]', String(id))
      return apiGet<MultiRemoteOpsPage>(`/opteemum/devices/multi/remote-ops?${p.toString()}`)
    },
    enabled: sorted.length > 0,
  })
}

export interface MultiCommandsDone {
  ok: boolean
  queued: number
}

/** `Opteemum::remoteCommands` peste selectie: cate un RAC pe fiecare masina. */
export function sendMultiRemoteCommands(
  ids: number[],
  group: MultiCommandGroup,
  actions: string[],
  inform: boolean
): Promise<MultiCommandsDone> {
  const fields: Record<string, PostValue> = {
    'devices[]': ids,
    'actions[]': actions,
    commands_group: group.group_name,
    commands_group_id: group.id,
  }
  if (inform) fields.inform_user = '1'
  return apiPost<MultiCommandsDone>('/opteemum/devices/multi/remote-ops/commands', fields)
}

/* --- Widgetul de selectie multipla, fila OS Patch Management ------------ */
/* Opteemum::multipleOsPatchManagement + multiple{Missing,Installed,Hidden}PatchesData
   + get{Missing,Installed,Hidden}PatchDevices + installPatchDevices / uninstallPatchDevices. */

export type MultiPatchKind = 'missing' | 'installed' | 'hidden'

export interface MultiPatchRow {
  DT_RowId: string
  patchId: number
  title: string
  description: string
  severity: string
  size: string
  /** HTML (bifa verde / minus rosu), ca in coloanele din opteemum.js. */
  require_source: string
  mandatory: string
  beta: string
  reboot: string
  devices: number
}

export interface MultiPatchTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  kind: MultiPatchKind
  data: MultiPatchRow[]
}

export interface MultiPatchTableRequest {
  kind: MultiPatchKind
  start: number
  length: number
  search: string
  orderColumn: number
  orderDir: 'asc' | 'desc'
}

/** Tabelul e pe TOATA firma (asa era si `companyPatchesTableData`), nu pe selectie. */
export function multiPatchesTableQuery(r: MultiPatchTableRequest) {
  return queryOptions({
    queryKey: ['opteemum', 'multi', 'os-patches', r],
    queryFn: () => {
      const p = new URLSearchParams({
        kind: r.kind,
        start: String(r.start),
        length: String(r.length),
        'search[value]': r.search,
        'order[0][column]': String(r.orderColumn),
        'order[0][dir]': r.orderDir,
      })
      return apiGet<MultiPatchTable>(`/opteemum/devices/multi/os-patches/table?${p.toString()}`)
    },
  })
}

export interface MultiPatchDevices {
  patch: number
  kind: MultiPatchKind
  missing: Array<{ id: number; name: string }>
  installed: Array<{ id: number; name: string }>
}

export function multiPatchDevicesQuery(kind: MultiPatchKind, patch: number) {
  return queryOptions({
    queryKey: ['opteemum', 'multi', 'os-patches', kind, patch],
    queryFn: () => apiGet<MultiPatchDevices>(`/opteemum/devices/multi/os-patches/${kind}/${patch}`),
  })
}

export interface MultiPatchDone {
  ok: boolean
  sent: number
  message?: string
}

export function installPatchDevices(patch: number, devices: number[]): Promise<MultiPatchDone> {
  return apiPost<MultiPatchDone>(`/opteemum/devices/multi/os-patches/${patch}/install`, { 'devices[]': devices })
}

export function uninstallPatchDevices(patch: number, devices: number[]): Promise<MultiPatchDone> {
  return apiPost<MultiPatchDone>(`/opteemum/devices/multi/os-patches/${patch}/uninstall`, { 'devices[]': devices })
}

/* --- Unassigned, fila Match (Opteemum::matchedDevices / matchedData / matchDevice) --- */

export interface MatchedRow extends Record<string, unknown> {
  DT_RowId: string
  ID: number
  oldDevice: number
  device_type: string
  /** HTML: punctul de stare (`pulsateMatched`) + pictograma tipului. */
  device_icon: string
  Denumire: string
  ownerId: number
  Nume: string | null
  Prenume: string | null
  UserLogat: string | null
  office: string | null
  DataAccesareDM: string | null
}

export function matchedTableQuery(id: number, r: Omit<PageRequest, 'draw'>) {
  return queryOptions({
    queryKey: ['opteemum', 'unassigned', id, 'matched', r],
    queryFn: () => {
      const p = new URLSearchParams({
        draw: '1',
        start: String(r.start),
        length: String(r.length),
        'search[value]': r.search,
        'order[0][column]': String(r.orderColumn),
        'order[0][dir]': r.orderDir,
      })
      return apiGet<TablePage<MatchedRow>>(`/opteemum/unassigned/${id}/matched-table?${p.toString()}`)
    },
  })
}

/** `Opteemum::matchDevice`: `id` e aparatul neatribuit (sters), `matched` cel vechi (pastrat). */
export function matchDevice(id: number, matched: number): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>(`/opteemum/unassigned/${id}/match`, { matchedDevice: matched })
}

export interface OwnerHit {
  ID: number
  Nume: string
  Prenume: string
  Email: string
}

/** `Opteemum::searchOwners`: proprietarii firmei dupa prefix (nume, prenume, email). */
export function searchOwners(q: string): Promise<OwnerHit[]> {
  return apiGet<{ data: OwnerHit[] }>(`/opteemum/owners/search?q=${encodeURIComponent(q)}`).then((r) => r.data)
}
